import React, { useState, useEffect } from "react";
import api from "services/api";
import InputBox from "elements/InputBox";
import { Form } from "react-bootstrap";
import { __ } from "services/Translator";
import PageHeadline from "../PageHeadline";
import PhoneInput from "components/PhoneInput";
import {
  validatePersonName,
  validateCity,
  validateStreetAddress,
  validatePostalCode,
  validatePhoneNumber,
  normalizePostalCode,
  parsePhoneForProfile,
  INPUT_FILTERS as FILTERS,
} from "utils/profileValidation";

export default function ProfileDetails(props) {
  const setSpinner = props.setSpinner;

  const [countries, setCountries] = useState([]);

  const getCountries = async () => {
    var response = await api.get("/countries");
    if (response.status === 200) {
      setCountries(
        response.data.map((val) => {
          return { label: val.nicename, value: val.code };
        })
      );
    } else {
      props.showAlert(null, response.data.message);
    }
  };

  const getProfile = async () => {
    setSpinner(true);
    var response = await api.get("/profile");
    setSpinner(false);
    if (response.status === 200) {
      setForm(response.data);
      // Lock state is decided from this server snapshot, never from the live
      // `form` (which mutates on every keystroke) — otherwise an empty field
      // would lock itself after the first character typed.
      setInitialProfile(response.data);
      if (props.setIsAnonymous) props.setIsAnonymous(parseInt(response.data.isAnonymous));
      setIsDataFetched(true);
    } else {
      props.showAlert(null, response.data.message);
    }
  };

  const submitProfile = async (ev) => {
    ev.preventDefault();
    setSpinner(true);
    var response = await api.post("/profile/save", {
      oldPassword: form.oldPassword,
      password: form.password,
      repassword: form.repassword,
    });
    setSpinner(false);
    if (response.status === 200) {
      props.showAlert("Success!!", response.data.message);
      setForm({ ...form, oldPassword: "", password: "", repassword: "" });
      setIsDataFetched(true);
    } else {
      props.showAlert(null, response.data.message, true);
    }
  };

  const [isDataFetched, setIsDataFetched] = useState(false);
  // Server snapshot used only to decide which fields are already filled (locked).
  const [initialProfile, setInitialProfile] = useState({});
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    dateOfBirth: "",
    currency: "",
    phoneCode: "",
    phone: "",
  });

  const updateForm = (value, field) => {
    setForm({ ...form, [field]: value });
  };

  const setFiltered = (field, filterKey, raw) => {
    const f = FILTERS[filterKey] || ((x) => x);
    updateForm(f(raw), field);
  };

  // Dynamic per-country state/province list; empty => country has none => hide.
  const [states, setStates] = useState([]);
  // Address is entered as street + house number, combined into the single
  // player_street_addr column on save.
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [phoneInfo, setPhoneInfo] = useState({ e164: "", valid: true });

  const toggleAnonymousSettings = async () => {
    props.setIsAnonymous(!parseInt(props.isAnonymous));
    props.setSpinner(true);
    var response = await api.post("/toggle-anonymous-settings", {
      isAnonymous: !props.isAnonymous,
    });
    props.setSpinner(false);
    if (response.status === 200) {
      props.showAlert("Success!!", response.data.message);
    } else {
      props.showAlert(null, response.data.message, true);
    }
  };

  const toggleTwoFASettings = async () => {
    setForm({ ...form, twoFaEnabled: !form.twoFaEnabled });
    props.setSpinner(true);
    var response = await api.post("/toggle-twofa-settings");
    props.setSpinner(false);
    if (response.status === 200) {
      props.showAlert("Success!!", response.data.message);
    } else {
      setForm({ ...form, twoFaEnabled: !!form.twoFaEnabled });
      props.showAlert(null, response.data.message, true);
    }
  };

  const getAllData = async (force) => {
    if (!isDataFetched || force) {
      setSpinner(true);
      await getCountries();
      await getProfile();
    }
  };

  useEffect(() => {
    getAllData();
  }, []);

  // Load the state/province list for the player's country. An empty result means
  // the country has no states (e.g. Netherlands), so the field is hidden.
  useEffect(() => {
    const cc = initialProfile.country || form.country;
    if (!cc) {
      setStates([]);
      return;
    }
    let alive = true;
    api.get("/states/" + cc).then((r) => {
      if (alive) setStates(r.status === 200 && Array.isArray(r.data) ? r.data : []);
    });
    return () => {
      alive = false;
    };
  }, [initialProfile.country]);

  // A progressive-profiling field is "locked" once it holds a value: the player
  // fills it in once here (or at the Cashier) and the backend save-basic endpoint
  // is write-once, so it can never be overwritten from the client afterwards.
  const filled = (val) => String(val ?? "").trim().length > 0;

  const [basicErrors, setBasicErrors] = useState({});

  const saveBasicDetails = async (ev) => {
    ev.preventDefault();
    const errors = {};
    const payload = { ...form };

    // Validate + normalise only the fields the player can still enter (the
    // locked ones are kept write-once server-side regardless of what we send).
    if (!filled(initialProfile.firstName)) {
      const r = validatePersonName(form.firstName, "first name");
      if (!r.valid) errors.firstName = r.error;
      else payload.firstName = r.value;
    }
    if (!filled(initialProfile.lastName)) {
      const r = validatePersonName(form.lastName, "last name");
      if (!r.valid) errors.lastName = r.error;
      else payload.lastName = r.value;
    }
    if (!filled(initialProfile.address)) {
      const rs = validateStreetAddress(street);
      if (!rs.valid) errors.address = rs.error;
      else if (!houseNumber.trim()) errors.address = __("Please enter your house number");
      else payload.address = (rs.value + " " + houseNumber.trim()).trim();
    }
    if (!filled(initialProfile.city)) {
      const r = validateCity(form.city);
      if (!r.valid) errors.city = r.error;
      else payload.city = r.value;
    }
    if (!filled(initialProfile.zip)) {
      const r = validatePostalCode(form.zip, form.country);
      if (!r.valid) errors.zip = r.error;
      else payload.zip = r.value;
    }
    // Phone is always editable and optional; only override the stored value when
    // the player actually entered one, and only when it is valid for its country.
    const phoneCheck = validatePhoneNumber(phoneInfo.e164, form.country);
    if (!phoneCheck.valid) {
      errors.phone = phoneCheck.error;
    } else if (phoneCheck.value) {
      const p = parsePhoneForProfile(phoneCheck.value, form.country || "US");
      payload.phoneCode = p.phoneCode;
      payload.phone = p.phone;
    }

    setBasicErrors(errors);
    if (Object.keys(errors).length) return;

    setSpinner(true);
    var response = await api.post("/profile/save-basic", payload);
    setSpinner(false);
    if (response.status === 200) {
      props.showAlert(__("Success"), response.data.message);
      setStreet("");
      setHouseNumber("");
      setPhoneInfo({ e164: "", valid: true });
      getProfile();
    } else if (response.data && response.data.errors) {
      setBasicErrors(response.data.errors);
    } else {
      props.showAlert(null, response.data.message, true);
    }
  };

  const countryLabel =
    countries?.filter((item) => item.value === form.country)[0]?.label || form.country;

  // Render helpers (plain functions, NOT nested components — a component defined
  // inside render remounts its inputs every keystroke and drops focus).

  // A locked, read-only field with a lock marker on its label.
  const lockedField = (label, value, size = "col-12 col-sm-6") => (
    <div className={size} key={label}>
      <div className="input_item">
        <div className="name">
          <i className="fal fa-lock" />
          {__(label)}
        </div>
        <input className="form-control name_value is-locked" value={value || ""} type="text" readOnly />
      </div>
    </div>
  );

  // An editable-once field: input while the server has no value yet, locked once
  // it has been saved (decided from the server snapshot, not the live form). The
  // optional filterKey blocks invalid characters as the player types.
  const enterOnceField = (label, field, filterKey = null, size = "col-12 col-sm-6", onBlur = null) => {
    if (filled(initialProfile[field])) {
      return lockedField(label, form[field], size);
    }
    return (
      <div className={size} key={field}>
        <div className="input_item">
          <div className="name">{__(label)}</div>
          <input
            className="form-control name_value"
            value={form[field] || ""}
            onChange={(e) =>
              filterKey ? setFiltered(field, filterKey, e.target.value) : updateForm(e.target.value, field)
            }
            onBlur={onBlur || undefined}
            type="text"
            placeholder={__("Enter your ") + __(label).toLowerCase()}
          />
          {basicErrors[field] && <div className="errorMsg">{basicErrors[field]}</div>}
        </div>
      </div>
    );
  };

  // Address is captured as street + house number when still open, and combined
  // into the single player_street_addr column on save.
  const addressBlock = () => {
    if (filled(initialProfile.address)) {
      return lockedField("Address", form.address, "col-12 col-sm-6");
    }
    return (
      <div className="col-12 col-sm-6" key="address">
        <div className="row">
          <div className="col-8">
            <div className="input_item">
              <div className="name">{__("Street")}</div>
              <input
                className="form-control name_value"
                value={street}
                onChange={(e) => setStreet(FILTERS.street(e.target.value))}
                type="text"
                placeholder={__("Street name")}
              />
            </div>
          </div>
          <div className="col-4">
            <div className="input_item">
              <div className="name">{__("No.")}</div>
              <input
                className="form-control name_value"
                value={houseNumber}
                onChange={(e) => setHouseNumber(FILTERS.house(e.target.value))}
                type="text"
                placeholder={__("85c")}
              />
            </div>
          </div>
        </div>
        {basicErrors.address && <div className="errorMsg">{basicErrors.address}</div>}
      </div>
    );
  };

  // State/province is shown only for countries that have one (dynamic per country).
  const stateBlock = () => {
    if (filled(initialProfile.state)) {
      return lockedField("State", form.state, "col-6 col-sm-3");
    }
    if (!states.length) return null;
    return (
      <div className="col-6 col-sm-3" key="state">
        <div className="input_item">
          <div className="name">{__("State / Province")}</div>
          <select
            className="form-control name_value select_box input_bg"
            value={form.state || ""}
            onChange={(e) => updateForm(e.target.value, "state")}
          >
            <option value="">{__("Select")}</option>
            {states.map((s) => (
              <option key={s.id} value={s.iso2 || s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const anyEnterOnceOpen = ["firstName", "lastName", "address", "city", "zip"].some(
    (f) => !filled(initialProfile[f])
  );

  return (
    <>
      <PageHeadline page_name="Account Details" />

      <div className="credentials">
        <div className="credentials__primary">
          <div className="id_no">
            <span className="id_no__label">{__("Casino ID")}</span>
            <span className="id">{form.pin || "—"}</span>
          </div>
        </div>
        <div className="credentials__settings">
          <div className="single_item switchItem">
            <span className="item_title">{__("Anonymous")}</span>
            <Form.Check
              className="form-check form-switch account-settings-switch"
              checked={props.isAnonymous}
              type="switch"
              onChange={toggleAnonymousSettings}
              id="anonymous_mode"
            />
          </div>
          <div className="single_item switchItem">
            <span className="item_title">{__("2FA")}</span>
            <Form.Check
              className="form-check form-switch account-settings-switch"
              checked={form.twoFaEnabled}
              type="switch"
              onChange={toggleTwoFASettings}
              id="twofa_mode"
            />
          </div>
        </div>
      </div>

      <div className="account-lock-note">
        <i className="fal fa-info-circle" />
        <span>
          {__(
            "You can fill in your details once. After you save them they are locked — to change a locked detail, contact support. You can update your phone number at any time."
          )}
        </span>
      </div>

      <div className="page_content_area profile-details-form">
        <div className="row">
          {enterOnceField("First Name", "firstName", "name")}
          {enterOnceField("Last Name", "lastName", "name")}
        </div>

        <div className="row">
          {lockedField("Email", form.email, "col-12 col-sm-6")}
          {lockedField("Birthday", form.dateOfBirth, "col-6 col-sm-3")}
          {lockedField("Country", countryLabel, "col-6 col-sm-3")}
        </div>

        <div className="row">
          <div className="col-12 col-sm-6">
            <div className="input_item">
              <div className="name">{__("Phone Number")}</div>
              <PhoneInput
                defaultCountry={(form.country || "US").toUpperCase()}
                value={(initialProfile.phoneCode || "") + (initialProfile.phone || "")}
                inputClassName="form-control name_value"
                onChange={(r) => setPhoneInfo({ e164: r.e164, valid: r.isValid })}
              />
              {basicErrors.phone && <div className="errorMsg">{basicErrors.phone}</div>}
            </div>
          </div>
        </div>

        <div className="row">
          {addressBlock()}
          {enterOnceField("City", "city", "city", "col-6 col-sm-3")}
          {enterOnceField("Zip Code", "zip", "zip", "col-6 col-sm-3", () =>
            updateForm(normalizePostalCode(form.zip, form.country), "zip")
          )}
        </div>

        <div className="row">
          {stateBlock()}
          {lockedField("Currency", form.currency, "col-6 col-sm-3")}
        </div>

        <div className="row">
          <div className="col-12">
            <button onClick={saveBasicDetails} className="commonBtn pTwoBtn">
              {anyEnterOnceOpen ? __("Save details") : __("Update phone number")}
            </button>
          </div>
        </div>

        <h5 className="subTitle profile-password-title">{__("Update Password")}</h5>

        <div className="row">
          <div className="col-12 col-sm-4">
            <div className="input_item">
              <div className="name">{__("Old Password")}</div>
              <InputBox
                className="form-control name_value"
                value={form.oldPassword || ""}
                onChange={(value) => updateForm(value, "oldPassword")}
                type="password"
              />
            </div>
          </div>
          <div className="col-12 col-sm-4">
            <div className="input_item">
              <div className="name">{__("New Password")}</div>
              <InputBox
                className="form-control name_value"
                value={form.password || ""}
                onChange={(value) => updateForm(value, "password")}
                type="password"
              />
            </div>
          </div>
          <div className="col-12 col-sm-4">
            <div className="input_item">
              <div className="name">{__("Re-type Password")}</div>
              <InputBox
                className="form-control name_value"
                value={form.repassword || ""}
                onChange={(value) => updateForm(value, "repassword")}
                type="password"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <button onClick={submitProfile} className="commonBtn pTwoBtn">
              {__("Update Password")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
